import React, { useEffect, useState } from "react";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import useNumericInput from "../../../hooks/useNumericInput";
import StoreAPI from "../../../api/StoreApi";
import StoreAdminApi from "../../../api/StoreAdminApi";
import AsyncSelect from "react-select/async";
import { useParams } from "react-router-dom";
import ImageUploader from "../../../components/partials/ImageUploader";
import StoreAdminAPI from "../../../api/StoreAdminApi";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import SubmitButton from "../../../components/partials/global/SubmitButton";

function Edit() {
  const { id } = useParams();
  useNumericInput();

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [stores, setStores] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedStore, setSelectedStore] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    image: "",
    store: "",
    username: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStoreChange = (option) => {
    setSelectedStore(option);

    setFormData((prev) => ({
      ...prev,
      store: option ? option.value : "",
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    getStoreAdminDetails(id);
  }, [id]);

  const loadStores = async (inputValue = "") => {
    const res = await StoreAPI.getListforDropdown({
      search: inputValue,
      limit: 10,
    });
    
    if (res.code === 200) {
      const options = res.data.map((store) => ({
        label: store.name,
        value: store._id,
      }));

      setStores(options);
      return options;
    }
  };

  const getStoreAdminDetails = async (id) => {
    try {
      const res = await StoreAdminApi.getStoreAdminDetails(id);

      if (res.code === 200) {
        const result = res.data;

        setFormData((prev) => ({
          ...prev,
          name: result.name || "",
          phone: result.mobile || "",
          email: result.email || "",
          username: result.username || "",
          image: result.image || "",
          store: result.storeId?._id || "",
        }));

        setOriginalData({
          name: result.name || "",
          phone: result.mobile || "",
          email: result.email || "",
          username: result.username || "",
          image: result.image || "",
          store: result.storeId?._id || "",
        });

        setSelectedStore({
          label: result?.store?.name,
          value: result?.store?._id,
        });
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = "Name is required";
    }

    if (!selectedStore) {
      newErrors.selectedStore = "Store Field is required";
    }

    if (formData.phone && formData.phone.length < 10) {
      newErrors.phone = "Enter Valid Phone Number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getChangedFields = (original, current) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (current[key] !== original[key]) {
        // empty / null image ignore
        if (key === "image" && !current[key]) return;
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    showLoader();
    setIsLoading(true);

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    const payload = getChangedFields(originalData, formData);

    if (Object.keys(payload).length === 0) {
      showError("No changes detected");
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const res = await StoreAdminAPI.updateStoreAdmin(id, payload);

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Edit Store Admin Details</h4>
            <h6>
              Update store administrator information to ensure seamless store
              operations and management.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Update Store Admin</h4>
          </div>
          <div className="card-body profile-body">
            <h5 className="mb-2">
              <i className="ti ti-user text-primary me-1" />
              Basic Information
            </h5>
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="store-admin-profile"
              />

              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Store <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-home"></i>
                      </span>
                      <AsyncSelect
                        cacheOptions
                        name="store"
                        defaultOptions={stores}
                        loadOptions={loadStores}
                        value={selectedStore}
                        onChange={handleStoreChange}
                        placeholder="Select Store"
                        isClearable
                      />
                    </div>
                    {errors.selectedStore && (
                      <span className="errors text-danger">
                        {errors.selectedStore}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.name && (
                      <span className="errors text-danger">{errors.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Phone Number</label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-phone"></i>
                      </span>
                      <input
                        type="text"
                        name="phone"
                        className="form-control only-numeric"
                        placeholder="Mobile Number"
                        maxLength={10}
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.phone && (
                      <span className="errors text-danger">{errors.phone}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      User Name<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        @
                      </span>
                      <input
                        type="text"
                        name="username"
                        className="form-control"
                        placeholder="Username"
                        value={formData.username}
                        readOnly
                        onChange={handleChange}
                      />
                    </div>
                    {errors.username && (
                      <span className="errors text-danger">
                        {errors.username}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <hr></hr>
              <h5 className="mb-2">
                <i className="ti ti-key text-primary me-1" />
                Login Information
              </h5>
              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Email Address<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-envelope"></i>
                      </span>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                        readOnly
                      />
                    </div>
                    {errors.email && (
                      <span className="errors text-danger">{errors.email}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Update Details"
                    loadingText="Updating..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Edit;
